import { toSignal } from "@angular/core/rxjs-interop";
import { MatError } from "@angular/material/form-field";
import { ActivatedRoute } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { startOfYear, subYears } from "date-fns";
import { Component, computed, effect, inject } from "@angular/core";
import { AbstractControl, ControlContainer, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";

import {
  InputTextComponent,
  DropdownComponent,
  DatepickerComponent,
  InputCountComponent,
  ButtonComponent,
  ValidationHandlerPipe,
  InputTextDropdownComponent,
} from "src/app/shared";
import { environment } from "src/environments/environment";
import { MembershipCardPayload } from "src/app/features/admin";
import { customValidations, LookupsService, TypedForm, TypedFormGroup } from "src/app/core";

const SHARED = [
  InputTextComponent,
  InputCountComponent,
  DropdownComponent,
  DatepickerComponent,
  ButtonComponent,
  InputTextDropdownComponent,
];

@Component({
  selector: "app-company-request-details",
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule, MatError, ValidationHandlerPipe, ...SHARED],
  templateUrl: "./company-request-details.component.html",
  styleUrl: "./company-request-details.component.scss",
})
export class CompanyRequestDetailsComponent {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private lookupsService = inject(LookupsService);
  private controlContainer = inject(ControlContainer);

  cardCost = 22.5;
  itemId = this.activatedRoute.snapshot.paramMap.get("id");
  countries = toSignal(this.lookupsService.getCountries(), { initialValue: [] });
  nationalities = toSignal(this.lookupsService.getNationalities(), { initialValue: [] });
  birthDateMaxDate = startOfYear(subYears(new Date(), environment.minAge));

  currentPhoneCountryCode = computed(() => {
    return "+968";

    // const countryCode = window.navigator.language.split("-").pop();
    // const country = this.countries().find((c) => c.code === countryCode);

    // if (!country) return null;
    // return country.phoneCode as string;
  });

  get form() {
    return this.controlContainer.control as FormGroup;
  }

  get totalNumberOfCards() {
    return this.form.get("totalNumberOfCards")?.value;
  }

  get totalAmount() {
    return this.form?.get("totalAmount");
  }

  get membershipCards() {
    return this.form.get("membershipCards") as FormArray<TypedFormGroup<MembershipCardPayload>>;
  }

  get totalNumberOfMembers() {
    return this.membershipCards.value.reduce((acc, card) => acc + (card.number ? card.number : 0), 0);
  }

  private get membershipCardPlanId() {
    return this.form.get("membershipCardPlanId");
  }

  get phoneCountryCode() {
    if (!this.countries().length && !this.form.get("user")?.get("countryCode")?.value) return null;

    const country = this.countries().find((c) => c.phoneCode === this.form.get("user")?.get("countryCode")?.value);

    if (!country) return null;
    return country.code;
  }

  constructor() {
    this.lookupsService.assignMembershipCardPlans();

    effect(() => {
      const membershipCardPlans = this.lookupsService.membershipCardPlans;

      if (membershipCardPlans) {
        const companyPlan = membershipCardPlans()?.find((plan) => plan.code === "1YC");
        this.membershipCardPlanId?.setValue(companyPlan?.id);
      }

      if (this.countries().length && !this.form.get("user")?.get("countryCode")?.value) {
        this.form.get("user")?.get("countryCode")?.setValue(this.currentPhoneCountryCode());
        this.getPhoneCountryCode(this.currentPhoneCountryCode()!);
      }
    });
  }

  ngOnInit(): void {
    //Called after the constructor, initializing input properties, and the first call to ngOnChanges.
    //Add 'implements OnInit' to the class.
    if (this.itemId) {
      setTimeout(() => this.totalAmount?.setValue(this.totalNumberOfCards * this.cardCost));
    }
  }

  /**
   * Adds a new membership card form to the membershipCards array,
   * applies the membershipCardsValidator to the array, and updates
   * the value and validity of the membershipCards array.
   */
  confirmCardsCount() {
    this.membershipCards.push(this.membershipCardForm());
    this.membershipCards.addValidators([this.membershipCardsCountValidator, this.membershipCardsNationalityValidator]);
    this.membershipCards.updateValueAndValidity();

    setTimeout(() => this.totalAmount?.setValue(this.totalNumberOfCards * this.cardCost));
  }

  /**
   * Adds a new membership card form to the list of membership cards.
   * After adding the new form, it updates the value and validity of the membership cards.
   */
  addCard() {
    this.membershipCards.push(this.membershipCardForm());
    this.membershipCards.updateValueAndValidity();
  }

  /**
   * Removes a membership card from the list at the specified index and updates the form's validity.
   *
   * @param index - The index of the membership card to remove.
   */
  removeCard(index: number) {
    this.membershipCards.removeAt(index);
    this.membershipCards.updateValueAndValidity();
  }

  getPhoneCountryCode(countryCode: string | number | boolean) {
    const country = this.countries().find((c) => c.phoneCode === countryCode);

    if (country) {
      const countryPhoneRegex = (country as any).phoneCodeRegex;

      this.form
        .get("user")
        ?.get("phone")
        ?.setValidators([Validators.required, customValidations.withCustomMobileNumberRegex(countryPhoneRegex)]);
    }
  }

  /**
   * Validates the number of membership cards against the total number of members.
   *
   * @returns An object with an `invalidCardsCount` property set to `true` if the total number of members exceeds the total number of cards, otherwise `null`.
   */
  private membershipCardsCountValidator = () => {
    if (this.totalNumberOfMembers !== this.totalNumberOfCards) return { invalidCardsCount: true };

    return null;
  };

  /**
   * Validator function to check for duplicate nationality IDs in a FormArray of membership cards.
   *
   * @param control - The form control to validate, expected to be a FormArray of TypedFormGroup<MembershipCardPayload>.
   * @returns An object with a `duplicateNationalityId` property set to true if duplicates are found, otherwise null.
   */
  private membershipCardsNationalityValidator = (control: AbstractControl) => {
    const cards = control as FormArray<TypedFormGroup<MembershipCardPayload>>;
    const nationalityIds = cards.controls.map((card) => card.value.nationalityId);

    const hasDuplicateNationalityId = nationalityIds.some((id, index) => nationalityIds.indexOf(id) !== index);
    if (hasDuplicateNationalityId) return { duplicateNationalityId: true };

    return null;
  };

  /**
   * Creates a form group for the membership card details.
   *
   * @returns A form group with controls for membership card number and nationality ID.
   *
   * - `number`: A form control initialized to 0 with required and minimum value validators.
   * - `nationalityId`: A form control initialized to null with a required validator.
   */
  private membershipCardForm() {
    return this.fb.group<TypedForm<MembershipCardPayload>>({
      number: this.fb.control(0, [Validators.required, Validators.min(1)]),
      nationalityId: this.fb.control(null, Validators.required),
    });
  }
}
